import axios from 'axios';

/**
 * REST path for the perfil ↔ sys-task junction (entidad con `allow`).
 * Ajusta si tu backend expone otro segmento (p. ej. nombre de entidad distinto).
 */
export const SYS_PERFIL_SYS_TASK_RESOURCE = 'api/sys-perfil-sys-tasks';

export interface SysPerfilSysTaskRow {
  id: number;
  allow: boolean;
  sysPerfil: { id?: number; nombre?: string; [k: string]: unknown };
  sysTask: { id: number; task?: string; modulo?: string; [k: string]: unknown };
  [k: string]: unknown;
}

/** Cuerpo PUT mismo esquema que GET; vínculos nuevos con `id: null`. Puede incluir auditoría como en el GET. */
export interface SysPerfilSysTaskPersistRow {
  id: number | null;
  allow: boolean;
  sysPerfil: { id?: number; nombre?: string; [k: string]: unknown };
  sysTask: { id: number; task?: string; modulo?: string; [k: string]: unknown };
  [k: string]: unknown;
}

/** Indica si el valor `allow` devuelto por el API significa permiso concedido. */
export function isAllowTrue(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === '') return false;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'si' || s === 'sí';
  }
  return false;
}

/**
 * Una fila puente por id de sys-task. Si el API devuelve varias filas para la misma tarea,
 * se usa la que tenga permiso concedido para que el checkbox coincida con `allow === true`.
 */
export function junctionRowsBySysTaskId(rows: SysPerfilSysTaskRow[]): Record<number, SysPerfilSysTaskRow> {
  const groups = new Map<number, SysPerfilSysTaskRow[]>();
  for (const row of rows) {
    const tid = row.sysTask?.id;
    if (tid == null) continue;
    const n = Number(tid);
    const list = groups.get(n) ?? ([] as SysPerfilSysTaskRow[]);
    list.push(row);
    groups.set(n, list);
  }
  const map: Record<number, SysPerfilSysTaskRow> = {};
  Array.from(groups.entries()).forEach(([taskId, list]) => {
    const granted = list.find((r: SysPerfilSysTaskRow) => isAllowTrue(r.allow));
    map[taskId] = granted ?? list[list.length - 1]!;
  });
  return map;
}

export function fetchPerfilTaskAssignments(perfilId: string | number) {
  return axios.get<SysPerfilSysTaskRow[]>(`api/sys-perfils/${perfilId}/tasks`).then(r => r.data);
}

/** Persiste todas las asignaciones tarea↔perfil (misma forma que GET). */
export function putPerfilTaskAssignments(perfilId: string | number, rows: SysPerfilSysTaskPersistRow[]) {
  return axios.put<SysPerfilSysTaskRow[]>(`api/sys-perfils/${perfilId}/tasks`, rows);
}

/** Filtra vínculos que corresponden al perfil edición (la API a veces devuelve mezcla). */
export function assignmentsForPerfil(rows: SysPerfilSysTaskRow[], perfilId: number) {
  return rows.filter(r => Number(r.sysPerfil?.id) === perfilId);
}

export function updateSysPerfilTaskAllow(row: SysPerfilSysTaskRow, allow: boolean) {
  const body = {
    id: row.id,
    sysPerfil: row.sysPerfil,
    sysTask: row.sysTask,
    allow,
  };
  return axios.put(`${SYS_PERFIL_SYS_TASK_RESOURCE}/${row.id}`, body);
}

export function createSysPerfilTaskAssignment(perfilId: number, sysTaskId: number, allow: boolean) {
  return axios.post<SysPerfilSysTaskRow>(SYS_PERFIL_SYS_TASK_RESOURCE, {
    sysPerfil: { id: perfilId },
    sysTask: { id: sysTaskId },
    allow,
  }).then(r => r.data);
}
